import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import CardContainer from '../../common/CardContainer';
import Landman from '../../../models/Landman';
import './LandmanDetailPage.scss';
import PagedResultSet from '../../../models/common/PagedResultSet';
import Project from '../../../models/Project';
import { ODataOptions } from '../../../util/ODataBuilder';
import PerformanceComment from 'src/models/PerformanceComment';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import ProjectStaff from 'src/models/ProjectStaff';
import Client from 'src/models/Client';
import TitleCard from '../components/TitleCard';
import LandmanStatusCard from '../components/LandmanStatusCard';
import LandmanContactInfoCard from '../components/LandmanContactInfoCard';
import LandmanRateCard from '../components/LandmanRateCard';
import LandmanExperienceCard from '../components/LandmanExperienceCard';
import LandmanCommentCard from '../components/LandmanCommentCard';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import AddCommentDialog from '../dialogs/AddCommentDialog';
import StaffLandmanDialog from '../dialogs/StaffLandmanDialog';
import DialogList from 'src/dialogs';

interface ILandmanDetailPageStateProps {
    landmanIsLoading: boolean;
    projectAdminStaff: PagedResultSet<ProjectAdministratorStaff> | undefined;
    landman: (id: number) => Landman;
    performanceComments: Array<PerformanceComment>;
    clients: PagedResultSet<Client> | undefined;
    projects: PagedResultSet<Project>;
    isStaffToProjectDialogShowing: boolean;
    isStaffingDetailsDialogShowing: boolean;
}

export interface ILandmanDetailPageDispatchProps {
    getLandman: (id: number) => void;
    getPerformanceComments: (userId: number) => void;
    getClients: () => void;
    getProjects: (clientNumber: number, options: ODataOptions) => void;
    getProjectAdministration: (clientNumber: number, projectNumber: number) => void;
    navigate: (path: string) => void;
    setDialogShowing: (dialogName: string) => void;
    hideDialog: () => void;
    staffToProject: (clientNumber: number, projectNumber: number, user: ProjectStaff) => void;
    createPerformanceComment: (comment: PerformanceComment) => void;
}

type ILandmanDetailPageProps = RouteComponentProps<{ id?: string }> & ILandmanDetailPageStateProps & ILandmanDetailPageDispatchProps;

class LandmanDetailPage extends React.Component<ILandmanDetailPageProps> {
    public componentDidMount() {
        const landmanId = parseInt(this.props.match.params.id, 10);
        this.props.getLandman(landmanId);
        this.props.getPerformanceComments(landmanId);
        this.props.getClients();
    }

    componentWillUnmount() {
        this.props.hideDialog();
    }

    onStaffToProjectClick = () => {
        this.props.setDialogShowing(DialogList.STAFF_LANDMAN);
    };

    onAddCommentClick = () => {
        this.props.setDialogShowing(DialogList.ADD_COMMENT);
    };

    public render() {
        const landman = this.props.landman(parseInt(this.props.match.params.id, 10));

        return (
            <>
                <TitleCard id="landman-detail" name={landman && `${landman.firstName} ${landman.lastName} `} />

                <LandmanStatusCard onStaffToProjectClick={this.onStaffToProjectClick} />

                <CardContainer id="landman-detail-row-1">
                    <LandmanContactInfoCard landman={landman} navigate={this.props.navigate} />
                    <LandmanRateCard landman={landman} navigate={this.props.navigate} />
                </CardContainer>

                <CardContainer id="landman-detail-row-2">
                    <LandmanExperienceCard />
                    <LandmanCommentCard
                        landman={landman}
                        performanceComments={this.props.performanceComments}
                        onAddCommentClick={this.onAddCommentClick}
                        navigate={this.props.navigate}
                    />
                </CardContainer>

                <AddCommentDialog landman={landman} createPerformanceComment={this.props.createPerformanceComment} />

                <StaffLandmanDialog
                    landman={landman}
                    staffToProject={this.props.staffToProject}
                    projects={this.props.projects}
                    clients={this.props.clients}
                    getProjects={this.props.getProjects}
                    getProjectAdministration={this.props.getProjectAdministration}
                    navigate={this.props.navigate}
                    projectAdminStaff={this.props.projectAdminStaff}
                />
            </>
        );
    }
}

const staffToProjectKey = 'staffToProject';
const staffDetailsKey = 'staffDetails';

const mapStateToProps = (state: IRootState) => ({
    landmanIsLoading: selectors.landman.isLandmenLoading(state),
    landman: (id: number) => selectors.landman.landman(id)(state),
    performanceComments: selectors.performanceComments.performanceComments(state),
    clients: selectors.client.clients(state),
    projects: selectors.project.projects(state),
    isStaffToProjectDialogShowing: selectors.layout.isDialogShowing(state, staffToProjectKey),
    isStaffingDetailsDialogShowing: selectors.layout.isDialogShowing(state, staffDetailsKey),
});

const mapDispatchToProps: ILandmanDetailPageDispatchProps = {
    staffToProject: (clientNumber: number, projectNumber: number, user: ProjectStaff) =>
        actions.user.addLandmanStaff(clientNumber, projectNumber, user),
    getLandman: (id: number) => actions.landman.getLandman(id),
    getPerformanceComments: (userId: number) => actions.performanceComment.getPerformanceComments(userId),
    getClients: () => actions.client.getClients(new ODataOptions()),
    getProjects: (clientNumber: number, options: ODataOptions) => actions.project.getProjects(clientNumber, options),
    getProjectAdministration: (clientNumber: number, projectNumber: number) =>
        actions.projectStaffing.getProjectAdmininistration(clientNumber, projectNumber),
    navigate: (path: string) => push(path),
    setDialogShowing: (dialogName: string) => actions.layout.showDialog(dialogName),
    hideDialog: () => actions.layout.hideDialog(),
    createPerformanceComment: (comment: PerformanceComment) => actions.performanceComment.createPerformanceComment(comment),
};

export default connect(mapStateToProps, mapDispatchToProps)(LandmanDetailPage);



// WEBPACK FOOTER //
// ./src/components/personnel/pages/LandmanDetailPage.tsx